import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import * as TrackingService from 'common/services/tracking.service';
import { Flex } from 'components/base/layout-components';
import {
  TRACKING,
  DASHBOARD_INTERNAL_TYPES,
  DASHBOARD_NAMES
} from 'common/constants/constants';
import Icon from 'components/base/Icon';
import { Tooltip } from '@material-ui/core';
import AddCustomDashboardEmptyState from 'images/add-custom-dashboard-empty-state.svg';
import { DashboardTab } from './DashboardTab';
import DashboardSummaryTooltip from '../performance/views/CustomReport/DashboardSummaryTooltip';
import UpgradeTooltip from '../performance/views/CustomReport/UpgradeTooltip';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import { CAN_EDIT_ACCOUNT_TYPES } from '@linearb/linjs-common';
import { useDispatch } from 'react-redux';
import { updatePinnedBoards } from 'state/callers';
import { SectionFavoriteDashboardsContainer } from './PerformanceSideBar.style';
import {
  StyledDashboardHeader,
  StyledDashboardItem
} from './CustomDashboardsSideBar.style';
import { getColor } from 'common/constants/_colors';
import { useHistory } from 'react-router';
import { EMPTY_DEFAULT_REPORT } from './reportsConfiguration';
import { boolPropsToClassNames } from 'common/utils/style.utils';
import { LBButton } from '@linearb/buzz';

const EmptyStateImage = styled.img`
  width: 100%;
`;

const CustomDashboardsSideBar = ({
  favoriteDashboards = [],
  newCustomDashBoard,
  selectedDashboard = null,
  onSelectDashboard = r => _.noop,
  onAddDashboard = () => {},
  loaded,
  pinnedDashboards,
  canCreateDashboards,
  maxDashboards,
  showEmptyState
}) => {
  const history = useHistory();
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const dispatch = useDispatch();

  const isCreatingNewDashboard = history.location.pathname.includes(
    EMPTY_DEFAULT_REPORT.id
  );

  useEffect(() => {
    // Set the side bar width whenever window size changes
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef]);

  return (
    <>
      {withPermissions({
        requiredPermissions: CAN_EDIT_ACCOUNT_TYPES,
        WrappedComponent: loaded && (
          <StyledDashboardHeader
            className={boolPropsToClassNames({
              selected: isCreatingNewDashboard
            })}
          >
            New Dashboard
            <Tooltip
              interactive
              placement='top'
              disableHoverListener={canCreateDashboards}
              title={
                canCreateDashboards ? (
                  ''
                ) : (
                  <UpgradeTooltip
                    title='Upgrade to create more custom dashboards'
                    text={[
                      `The free plan includes ${maxDashboards} custom dashboards - including private dashboards that may be hidden to you. Upgrade to add more.`
                    ]}
                  />
                )
              }
            >
              <span>
                <LBButton
                  size='icon'
                  data-cy='add-custom-dashboard'
                  disabled={!canCreateDashboards || isCreatingNewDashboard}
                  variant={
                    favoriteDashboards.length === 0 && !newCustomDashBoard
                      ? 'default'
                      : 'outline'
                  }
                  onClick={onAddDashboard}
                >
                  <Icon
                    name='add'
                    {...(favoriteDashboards.length === 0 && !newCustomDashBoard
                      ? { color: 'white' }
                      : { alpha: 0.5 })}
                  />
                </LBButton>
              </span>
            </Tooltip>
          </StyledDashboardHeader>
        )
      })}

      {loaded && showEmptyState && (
        <EmptyStateImage
          src={AddCustomDashboardEmptyState}
          alt='custom dashboard empty state'
        />
      )}
      <Flex direction='column'>
        <StyledDashboardItem style={{ color: getColor('osloGray') }}>
          My Favorites
          <Icon name='Pinned' color='osloGray' alpha='0.7' />
        </StyledDashboardItem>
        <SectionFavoriteDashboardsContainer ref={containerRef}>
          {favoriteDashboards.map((dashboard, idx) => {
            const dashboardsTypesToNamesMap = {
              [DASHBOARD_INTERNAL_TYPES.TEAM_GOALS]: DASHBOARD_NAMES.TEAM_GOALS,
              [DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS]: dashboard.name
            };
            const isMetricReport = dashboard.type === 'metrics';
            const isTeamGoalsReport =
              dashboard.internal_type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS;
            const isCustomPmReport =
              dashboard.internal_type ===
              DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS;
            const shouldHaveBetaBAdge = dashboard.is_beta;
            return (
              <Tooltip
                interactive
                disableHoverListener={!isMetricReport}
                key={`${idx}-default`}
                title={
                  isMetricReport ? (
                    <DashboardSummaryTooltip
                      dashboard={dashboard}
                      isTeamGoalsReport={isTeamGoalsReport}
                    />
                  ) : (
                    ''
                  )
                }
                placement='right'
              >
                <div>
                  <DashboardTab
                    key={`${idx}-custom`}
                    dashboardKey={`${idx}-custom`}
                    selected={
                      selectedDashboard && dashboard.id === selectedDashboard.id
                    }
                    onClick={() => {
                      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                        page: TRACKING.PAGES.METRICS,
                        click_type: dashboard.name,
                        dashboard_name: dashboard.name,
                        dashboard_id: dashboard.id,
                        internal_type: dashboard.internal_type,
                        auto_created: _.get(
                          dashboard,
                          'extended_data.auto_created',
                          false
                        )
                      });
                      return onSelectDashboard(dashboard);
                    }}
                    dashboardName={
                      dashboardsTypesToNamesMap[dashboard.internal_type] ??
                      dashboard.name
                    }
                    isCustomPmReport={isCustomPmReport}
                    dashboardId={dashboard.id}
                    pinnedDashboards={pinnedDashboards}
                    onPinDashboard={dashboards => {
                      updatePinnedBoards(dashboards)(dispatch);
                    }}
                    isPublic={dashboard.is_public}
                    enablePin={
                      !isTeamGoalsReport &&
                      (!isCustomPmReport || !dashboard.auto_created)
                    }
                    {...(shouldHaveBetaBAdge
                      ? {
                          badge: 'beta'
                        }
                      : {})}
                    containerWidth={containerWidth}
                  />
                </div>
              </Tooltip>
            );
          })}
        </SectionFavoriteDashboardsContainer>
      </Flex>
    </>
  );
};

export default CustomDashboardsSideBar;
